import { createFileRoute, Link } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { BarChart3, Globe2 } from "lucide-react"
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { MacroReportDrawer } from "@/components/dashboard/MacroReportDrawer"
import { SeasonalityCard } from "@/components/dashboard/SeasonalityCard"
import { useGoldPrice } from "@/hooks/useGoldPrice"
import { Button } from "@/components/ui/button"
import { loadMacroLensInputs } from "@/lib/macrolens-inputs"
import { matchedSurprise } from "@/lib/macro-surprise"
import {
  aggregate,
  classify,
  scoreCot,
  scoreCpi,
  scoreLiquidity,
  liquidityInputFrom,
  scoreNfp,
  scoreOptions, scoreRealYield,
  scorePremiumDiscount,
  scoreRetail,
  scoreSeasonality,
  scoreStructure,
  type PillarId,
  type StructureInput,
} from "@/lib/macrolens"

export const Route = createFileRoute("/_authenticated/dashboard/macrolens")({
  head: () => ({
    meta: [
      { title: "XAU MacroLens™ Terminal | XAU Pro Academy" },
      { name: "description", content: "Gold confluence terminal: COT, CPI, NFP, GLD options, retail sentiment, seasonality and HTF structure in one score." },
      { property: "og:title", content: "XAU MacroLens™ Terminal | XAU Pro Academy" },
      { property: "og:description", content: "Macro and technical confluence for XAUUSD, scored from -100 to +100." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  loader: loadMacroLensInputs,
  component: MacroLensTerminal,
})

const fmt = (v: number | null | undefined, d = 1, suffix = "") => (v == null ? "n/a" : `${v.toFixed(d)}${suffix}`)
const signed = (v: number | null) => (v == null ? "n/a" : `${v > 0 ? "+" : ""}${v}`)

function tone(v: number | null) {
  if (v == null) return "text-muted-foreground"
  if (v >= 20) return "text-emerald"
  if (v <= -20) return "text-destructive"
  return "text-gold"
}

const STRUCTURE_OPTIONS: StructureInput[] = ["Bullish structure", "Bullish pullback", "Consolidating", "Bearish pullback", "Bearish structure"]
const PILLAR_NAMES: Record<PillarId, string> = {
  cot: "CFTC COT positioning",
  cpi: "US CPI inflation",
  nfp: "NFP and employment",
  realYield: "US 10Y real yield · TIPS",
  options: "Gold options positioning · GLD",
  retail: "Retail contrarian sentiment",
  seasonality: "Seasonality vector",
  structure: "HTF SMC structure",
  premiumDiscount: "Premium and discount matrix",
  liquidity: "Liquidity sweeps",
}

function sessionNow(d = new Date()) {
  const h = d.getUTCHours() + d.getUTCMinutes() / 60
  const day = d.getUTCDay()
  if (day === 6 || (day === 0 && h < 22) || (day === 5 && h >= 21)) return "Market closed"
  if (h >= 12 && h < 16) return "London / NY overlap"
  if (h >= 7 && h < 12) return "London"
  if (h >= 16 && h < 21) return "New York"
  return "Asian"
}

const ageLabel = (iso: string | null | undefined) => {
  if (!iso) return "n/a"
  const ms = Date.now() - Date.parse(iso)
  if (!Number.isFinite(ms)) return iso
  const h = ms / 3600000
  if (h < 1) return `${Math.max(1, Math.round(ms / 60000))}m ago`
  if (h < 48) return `${Math.round(h)}h ago`
  return `${Math.round(h / 24)}d ago`
}

function MacroLensTerminal() {
  const { cot, cpi, nfp, season, retail, intel, d1, options, surprises, realYield, w1Label } = Route.useLoaderData()
  const ry = realYield?.latest ?? null
  const live = useGoldPrice()

  const lastCot = cot?.weeks.at(-1)
  const prevCot = cot?.weeks.at(-2)
  const cotChange = lastCot && prevCot ? lastCot.mmNet - prevCot.mmNet : null
  const cpiRows = (cpi?.months ?? []).filter((m) => m.headlineYoY != null)
  const cpiLast = cpiRows.at(-1)
  const cpiPrev = cpiRows.at(-2)
  const nfpRows = (nfp?.months ?? []).filter((m) => m.payrollsK != null)
  const nfpLast = nfpRows.at(-1)
  const nfpPrev = nfpRows.at(-2)
  const cpiSurprise = matchedSurprise(surprises?.cpi, cpiLast?.period)
  const nfpSurprise = matchedSurprise(surprises?.nfp, nfpLast?.period)
  const month = season?.months.find((m) => m.month === season.currentMonth)
  const longPct = retail?.latest?.longPct ?? null
  const price = live.price ?? intel?.price ?? null
  const d1Row = intel?.structure.find((s) => s.tf === "D1")
  const h4Row = intel?.structure.find((s) => s.tf === "H4")
  const rangeLow = d1.length ? Math.min(...d1.map((c) => c.l)) : null
  const rangeHigh = d1.length ? Math.max(...d1.map((c) => c.h)) : null
  const liq = intel?.liquidity
  const liqInput = liquidityInputFrom(liq, price)

  const base = {
    longPct,
    cpiDelta: 0,
    d1: (d1Row?.label as StructureInput) ?? null,
    h4: (h4Row?.label as StructureInput) ?? null,
    w1: (w1Label as StructureInput | null | undefined) ?? null,
    pricePos: rangeLow != null && rangeHigh != null && price != null ? Math.round(((price - rangeLow) / (rangeHigh - rangeLow)) * 100) : null,
  }
  const [sim, setSim] = useState(base)
  const [openReport, setOpenReport] = useState<PillarId | null>(null)
  const simActive = JSON.stringify(sim) !== JSON.stringify(base)

  const build = (o: typeof base) => {
    const simPrice = o.pricePos != null && rangeLow != null && rangeHigh != null ? rangeLow + (o.pricePos / 100) * (rangeHigh - rangeLow) : price
    return {
      cot: scoreCot(cot?.mmIndex3y ?? null, cotChange),
      cpi: scoreCpi(cpiLast?.headlineYoY != null ? cpiLast.headlineYoY + o.cpiDelta : null, cpiPrev?.headlineYoY ?? null, cpiLast?.coreMoM != null ? cpiLast.coreMoM + o.cpiDelta : null, cpiSurprise),
      nfp: scoreNfp(nfpLast?.payrollsK ?? null, nfpLast?.unemployment ?? null, nfpPrev?.unemployment ?? null, nfpSurprise),
      realYield: realYield?.stale ? null : scoreRealYield(ry?.real ?? null, ry?.monthlyChange ?? null, ry?.date),
      options: options?.stale ? null : scoreOptions(options?.summary?.pcrOi ?? null, options?.summary?.asOf),
      retail: scoreRetail(o.longPct),
      seasonality: scoreSeasonality(month?.avg10y ?? null),
      structure: scoreStructure(o.d1, o.h4, o.w1),
      premiumDiscount: scorePremiumDiscount(simPrice, rangeLow, rangeHigh, scoreStructure(o.d1, o.h4, o.w1)),
      liquidity: scoreLiquidity(liqInput),
    } as Record<PillarId, number | null>
  }
  const result = aggregate(build(base))
  const simResult = useMemo(() => aggregate(build(sim)), [JSON.stringify(sim), price])

  const fresh = [
    { k: "CFTC COT", v: lastCot?.date ?? "n/a", note: "weekly, 3 day lag" },
    { k: "CPI", v: cpiLast?.period ?? "n/a", note: "monthly, BLS" },
    { k: "NFP", v: nfpLast?.period ?? "n/a", note: "monthly, BLS" },
    { k: "Real yield", v: ry?.date ?? "n/a", note: realYield?.stale ? "last good, excluded" : "FRED daily" },
    { k: "GLD options", v: ageLabel(options?.summary?.asOf), note: options?.stale ? "last good, excluded" : "CBOE delayed" },
    { k: "Retail", v: ageLabel(retail?.latest?.at), note: "Myfxbook sample" },
    { k: "D1 range", v: d1.length ? `${d1.length} sessions` : "n/a", note: "closed candles" },
  ]

  const pillarDetail: Record<PillarId, { rows: [string, string][]; spark?: number[]; href?: string; why: string }> = {
    cot: {
      rows: [["MM net", lastCot ? lastCot.mmNet.toLocaleString("en-US") : "n/a"], ["Weekly change", cotChange == null ? "n/a" : (cotChange > 0 ? "+" : "") + cotChange.toLocaleString("en-US")], ["3y index", fmt(cot?.mmIndex3y, 1, "%")], ["As of", lastCot?.date ?? "n/a"]],
      spark: cot?.weeks.slice(-16).map((w) => w.mmNet),
      href: "/gold-cot-report",
      why: "Percentile 40 to 60 is neutral. Extremes score strongly, and a weekly flow against the reading trims the score by 40%.",
    },
    cpi: {
      rows: [["Headline YoY", fmt(cpiLast?.headlineYoY, 1, "%")], ["Prior YoY", fmt(cpiPrev?.headlineYoY, 1, "%")], ["Core MoM", fmt(cpiLast?.coreMoM, 1, "%")], ["Surprise vs consensus", cpiSurprise == null ? "Unavailable" : `${signed(cpiSurprise)} pp`], ["Reference", cpiLast?.period ?? "n/a"]],
      spark: cpiRows.slice(-16).map((m) => m.headlineYoY ?? 0),
      href: "/gold-inflation-cpi",
      why: "Hotter inflation lifts Fed rate expectations and real yields, pressuring gold. Cooling inflation can support it. When matched consensus is available, the score blends 70% trend and 30% release surprise. Missing consensus retains the trend reading.",
    },
    nfp: {
      rows: [["Payrolls", nfpLast?.payrollsK == null ? "n/a" : `${nfpLast.payrollsK}K`], ["Unemployment", fmt(nfpLast?.unemployment, 1, "%")], ["Prior unemployment", fmt(nfpPrev?.unemployment, 1, "%")], ["Surprise vs consensus", nfpSurprise == null ? "Unavailable" : `${signed(nfpSurprise)}K`], ["Wages YoY", fmt(nfpLast?.wagesYoY, 1, "%")]],
      spark: nfpRows.slice(-16).map((m) => m.payrollsK ?? 0),
      href: "/gold-nfp-employment",
      why: "Payrolls below 150K and rising unemployment can support gold. Matched surprises blend 70% labour conditions and 30% actual minus consensus. Missing consensus retains the base reading.",
    },
    realYield: {
      rows: [["10Y real yield", fmt(ry?.real, 2, "%")], ["Monthly change", ry?.monthlyChange == null ? "n/a" : `${signed(Math.round(ry.monthlyChange * 100) / 100)} pp`], ["Weekly change", ry?.weeklyChange == null ? "n/a" : `${signed(Math.round(ry.weeklyChange * 100) / 100)} pp`], ["10Y nominal", fmt(ry?.nominal, 2, "%")]],
      why: "Gold pays no yield, so the inflation-adjusted Treasury yield is its opportunity cost. A rising real yield pressures gold and a falling one supports it. Monthly momentum carries most of the score, the level adds a smaller tilt. Readings older than ten days are excluded.",
    },
    options: {
      rows: [["Put/Call OI", fmt(options?.summary?.pcrOi, 3)], ["Call OI", options?.summary?.callOi.toLocaleString("en-US") ?? "n/a"], ["Put OI", options?.summary?.putOi.toLocaleString("en-US") ?? "n/a"], ["As of", options?.summary?.asOf ?? "n/a"]],
      href: "/gold-options-sentiment",
      why: "CBOE delayed GLD open interest is an ETF positioning proxy, not direct XAUUSD flow. Call-heavy OI gives a modest positive tilt and put-heavy OI a modest negative tilt, capped at 40 of 100. Open interest does not identify buyers, sellers or dealer exposure. Walls, max pain and IV30 do not drive the score. No directional accuracy is implied.",
    },
    retail: {
      rows: [["Long share", longPct == null ? "n/a" : `${longPct}%`], ["Short share", longPct == null ? "n/a" : `${100 - longPct}%`], ["Long avg entry", fmt(retail?.latest?.longAvg, 2)], ["Short avg entry", fmt(retail?.latest?.shortAvg, 2)]],
      spark: retail?.history.slice(-30).map((h) => h.longPct),
      href: "/gold-retail-sentiment",
      why: "Contrarian. A crowd above 65% long is liquidity for a move lower, below 35% long for a move higher.",
    },
    seasonality: {
      rows: [["Month", month?.monthName ?? "n/a"], ["10y average", fmt(month?.avg10y, 2, "%")], ["Positive years", fmt(month?.pos10y, 0, "%")], ["Month to date", fmt(month?.mtdPct, 2, "%")]],
      href: "/gold-seasonality",
      why: "Lowest weight. A ten year tendency, never a reason to trade on its own.",
    },
    structure: {
      rows: (intel?.structure ?? []).map((s) => [s.tf, s.label] as [string, string]),
      why: "D1 and H4 body-close structure, equally weighted. Pullbacks score half of a confirmed structure.",
    },
    premiumDiscount: {
      rows: [["Price", fmt(price, 2)], ["20D low", fmt(rangeLow, 2)], ["20D high", fmt(rangeHigh, 2)], ["Position", base.pricePos == null ? "n/a" : `${base.pricePos}% of range`]],
      spark: d1.map((c) => c.c),
      why: "Below equilibrium is discount and favours longs, above is premium and favours shorts.",
    },
    liquidity: {
      rows: [["Asian high", `${fmt(liq?.asianHigh, 2)}${liq?.asianHighSwept ? " swept" : ""}`], ["Asian low", `${fmt(liq?.asianLow, 2)}${liq?.asianLowSwept ? " swept" : ""}`], ["Prior day high", `${fmt(liq?.prevDayHigh, 2)}${liq?.prevDayHighSwept ? " swept" : ""}`], ["Prior day low", `${fmt(liq?.prevDayLow, 2)}${liq?.prevDayLowSwept ? " swept" : ""}`]],
      why: "Sell-side swept and reclaimed or buy-side taken and held is bullish. Buy-side swept and rejected or sell-side taken and held is bearish.",
    },
  }

  const s = result.score ?? 0
  const playbook = {
    bias: s >= 20 ? "Long interest" : s <= -20 ? "Short interest" : "No directional preference",
    risk: result.alignment === "aligned" && Math.abs(s) >= 20 ? "Standard risk" : "Reduced risk",
    setups:
      s >= 20
        ? "Discount POIs after a sell-side sweep with displacement and M15 BOS."
        : s <= -20
          ? "Premium POIs after a buy-side sweep with displacement and M15 BOS."
          : "Range extremes only, with full liquidity confirmation. Missing a trade is acceptable.",
  }

  return (
    <div className="relative">
    <ClimateGlow score={result.score} />
    <div className="relative container mx-auto max-w-6xl px-4 py-8 md:py-10">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-border/60 bg-card/40 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em]">
        <span className="text-gold">XAU MacroLens™</span>
        <span className="text-muted-foreground">XAUUSD <span className={`text-sm tabular-nums ${live.direction === "up" ? "text-emerald" : live.direction === "down" ? "text-destructive" : "text-foreground"}`}>{fmt(price, 2)}</span></span>
        <span className="text-muted-foreground">Session <span className="text-foreground">{sessionNow()}</span></span>
        <span className="text-muted-foreground">Coverage <span className="text-foreground">{result.coverage}%</span></span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-7">
        {fresh.map((f) => (
          <div key={f.k} className="rounded-md border border-border/50 bg-background/40 px-3 py-2">
            <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">{f.k}</div>
            <div className="mt-0.5 font-mono text-xs tabular-nums text-foreground">{f.v}</div>
            <div className="text-[10px] text-muted-foreground">{f.note}</div>
          </div>
        ))}
      </div>

      <h1 className="mt-8 font-display text-3xl font-semibold md:text-4xl">Gold confluence terminal</h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Fundamentals and HTF structure scored on one scale. Context for decisions, not an entry signal.</p>

      <section className="mt-6 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
        <div className="relative rounded-xl border border-border/60 bg-card/40 p-6">
          {/* Tactical corner marks */}
          {["left-2 top-2 border-l border-t", "right-2 top-2 border-r border-t", "bottom-2 left-2 border-b border-l", "bottom-2 right-2 border-b border-r"].map((pos) => (
            <span key={pos} aria-hidden className={`absolute h-3 w-3 border-gold/50 ${pos}`} />
          ))}
          <div className="flex items-baseline justify-between">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">XAU MacroLens™ Score</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground/60">System Confluence Terminal · Matrix-01</div>
          </div>
          <Gauge value={result.score} />
          <div className="-mt-1 flex flex-col items-center">
            <div className="rounded-full border border-border/60 bg-background/60 px-7 py-2 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)] backdrop-blur-sm">
              <span className={`font-mono text-5xl font-semibold tabular-nums ${tone(result.score)}`}>{signed(result.score)}</span>
            </div>
            <div className="mt-2 text-base font-medium">{result.classification ?? "Insufficient data"}</div>
            <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-border/50 bg-background/40 px-3 py-1">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-60" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-gold" />
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                {result.coverage >= 90 ? "Full data coverage" : result.coverage >= 60 ? "Partial coverage" : "Low coverage"} · {result.coverage}% coverage
              </span>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4">
            {(["macro", "technical"] as const).map((arm) => {
              const v = arm === "macro" ? result.macro : result.technical
              const Icon = arm === "macro" ? Globe2 : BarChart3
              return (
                <div key={arm} className="rounded-md border border-border/50 bg-background/40 p-3">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      <Icon className="h-3.5 w-3.5 text-gold/70" aria-hidden />
                      {arm === "macro" ? "Macro arm" : "Technical arm"}
                    </span>
                    <span className={`font-mono text-lg tabular-nums ${tone(v)}`}>{signed(v)}</span>
                  </div>
                  <ScoreBar value={v} />
                  <div className="mt-1 flex items-baseline justify-between">
                    <span className="text-[11px] text-muted-foreground">{v == null ? "No data" : classify(v)}</span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground/60">{result.alignment}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="rounded-xl border border-gold/25 bg-gold-muted p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Alignment · {result.alignment}</div>
            <p className="mt-2 text-sm leading-relaxed">{result.note}</p>
          </div>
          <div className="flex-1 rounded-xl border border-border/60 bg-card/40 p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Desk operational playbook</div>
            <dl className="mt-3 space-y-3 text-sm">
              <div><dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Preferred side</dt><dd className={`mt-0.5 font-medium ${tone(result.score)}`}>{playbook.bias}</dd></div>
              <div><dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Risk posture</dt><dd className="mt-0.5">{playbook.risk}</dd></div>
              <div><dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Qualified setups</dt><dd className="mt-0.5 text-muted-foreground">{playbook.setups}</dd></div>
              {intel?.reading.warning && <div><dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Tape caution</dt><dd className="mt-0.5 text-muted-foreground">{intel.reading.warning}</dd></div>}
            </dl>
          </div>
        </div>
      </section>

      <div className="mt-8 grid items-start gap-6 lg:grid-cols-2">
      {(["macro", "technical"] as const).map((arm) => (
        <section key={arm}>
          <h2 className="flex items-baseline justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
            <span>{arm === "macro" ? "Fundamental pillars · 35%" : "Technical pillars · 65%"}</span>
            <span className={`text-sm tabular-nums ${tone(arm === "macro" ? result.macro : result.technical)}`}>{signed(arm === "macro" ? result.macro : result.technical)}</span>
          </h2>
          <div className="mt-3 grid gap-3">
            {result.pillars.filter((p) => p.arm === arm).map((p) => {
              const d = pillarDetail[p.id]
              return (
                <details key={p.id} className="group rounded-lg border border-border/60 bg-card/30 p-4 open:border-gold/40">
                  <summary className="cursor-pointer list-none">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium">{PILLAR_NAMES[p.id]}</div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Weight {p.weight}% · <span className="group-open:hidden">Expand</span><span className="hidden group-open:inline">Collapse</span></div>
                      </div>
                      <div className="flex items-center gap-3">
                        {d.spark && d.spark.length > 1 && <Spark data={d.spark} />}
                        <span className={`font-mono text-2xl tabular-nums ${tone(p.score)}`}>{signed(p.score)}</span>
                      </div>
                    </div>
                    <ScoreBar value={p.score} />
                  </summary>
                  <div className="mt-4 border-t border-border/50 pt-3">
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                      {d.rows.map(([k, v]) => (
                        <div key={k}><dt className="font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground">{k}</dt><dd className="font-mono text-xs tabular-nums">{v}</dd></div>
                      ))}
                    </dl>
                    <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{d.why}</p>
                    {d.href && <Button variant="link" type="button" onClick={() => setOpenReport(p.id)} className="mt-2 inline-block font-mono text-[10px] uppercase tracking-[0.18em] text-gold hover:underline">Full report</Button>}
                  </div>
                </details>
              )
            })}
          </div>
        </section>
      ))}
      </div>


      <section className="mt-10 rounded-xl border border-border/60 bg-card/40 p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">What-if simulator</div>
            <p className="mt-1 text-sm text-muted-foreground">Move the inputs to see how the confluence reading would change. Live data is untouched.</p>
          </div>
          <button type="button" disabled={!simActive} onClick={() => setSim(base)} className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold disabled:opacity-40 hover:underline">Reset to live</button>
        </div>
        <div className="mt-5 grid gap-6 md:grid-cols-[1.4fr_1fr]">
          <div className="space-y-5">
            <SimSlider label="Retail long share" value={sim.longPct ?? 50} min={10} max={90} unit="%" onChange={(v) => setSim({ ...sim, longPct: v })} />
            <SimSlider label="CPI trend shift vs current reading" value={sim.cpiDelta} min={-0.5} max={0.5} step={0.1} unit=" pp" onChange={(v) => setSim({ ...sim, cpiDelta: Math.round(v * 10) / 10 })} />
            <SimSlider label="Price position in 20D range" value={sim.pricePos ?? 50} min={0} max={100} unit="%" onChange={(v) => setSim({ ...sim, pricePos: v })} />
            <div className="grid grid-cols-2 gap-3">
              {(["d1", "h4"] as const).map((tf) => (
                <label key={tf} className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{tf.toUpperCase()} structure</span>
                  <select value={sim[tf] ?? "Consolidating"} onChange={(e) => setSim({ ...sim, [tf]: e.target.value as StructureInput })} className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm">
                    {STRUCTURE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-border/50 bg-background/40 p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Simulated MacroLens™ Score</div>
            <div className={`mt-1 font-mono text-4xl font-semibold tabular-nums ${tone(simResult.score)}`}>{signed(simResult.score)}</div>
            <div className="text-sm">{simResult.classification ?? "Insufficient data"}</div>
            <div className="mt-2 font-mono text-[11px] text-muted-foreground">Live {signed(result.score)} · change {result.score != null && simResult.score != null ? signed(simResult.score - result.score) : "n/a"}</div>
            <div className="mt-3 grid grid-cols-2 gap-2 font-mono text-[11px]">
              <span className="text-muted-foreground">Macro <span className={tone(simResult.macro)}>{signed(simResult.macro)}</span></span>
              <span className="text-muted-foreground">Technical <span className={tone(simResult.technical)}>{signed(simResult.technical)}</span></span>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{simResult.note}</p>
          </div>
        </div>
      </section>

      <p className="mt-8 text-[11px] text-muted-foreground">
        COT is published weekly with a three day lag. CPI and NFP update monthly. Retail sentiment is a sampled broker community, not the full market. Missing pillars are excluded and weights renormalised.
      </p>
      <MacroReportDrawer report={openReport === "cot" || openReport === "cpi" || openReport === "nfp" ? openReport : null} onClose={() => setOpenReport(null)} />
      <Sheet open={openReport === "seasonality" || openReport === "retail" || openReport === "options"} onOpenChange={(o) => { if (!o) setOpenReport(null) }}>
        <SheetContent className="flex h-full w-full flex-col gap-0 p-0 sm:max-w-3xl motion-reduce:animate-none motion-reduce:transition-none">
          <SheetHeader className="shrink-0 border-b border-border px-5 py-5 pr-12 text-left">
            <p className="font-mono text-[10px] uppercase text-gold">Fundamentals · research desk</p>
            <SheetTitle className="font-display text-xl">{openReport === "options" ? "Gold options · GLD" : openReport === "retail" ? "Retail sentiment" : "Gold seasonality"}</SheetTitle>
            <SheetDescription>{openReport === "options" ? "CBOE delayed GLD chain · ETF proxy, not spot execution levels" : openReport === "retail" ? "Myfxbook XAUUSD community sample · contrarian context, not entry signals" : "XAUUSD spot monthly history · context, not entry signals"}</SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5">
            {openReport === "seasonality" && <SeasonalityCard />}
            {openReport === "options" && <div className="space-y-5">
              <dl className="grid grid-cols-2 gap-4">{pillarDetail.options.rows.slice(0, 4).map(([k, v]) => <div key={k}><dt className="font-mono text-[10px] uppercase text-muted-foreground">{k}</dt><dd className="mt-1 break-words font-mono text-lg">{v}</dd></div>)}</dl>
              <p className="text-sm leading-relaxed text-muted-foreground">{pillarDetail.options.why}</p>
              <dl className="grid grid-cols-2 gap-4">{[["GLD call wall", fmt(options?.summary?.callWall?.strike, 2)], ["GLD put wall", fmt(options?.summary?.putWall?.strike, 2)], ["GLD max pain", fmt(options?.summary?.maxPain, 2)], ["IV30 (source)", fmt(options?.summary?.iv30, 2)]].map(([k, v]) => <div key={k}><dt className="font-mono text-[10px] uppercase text-muted-foreground">{k}</dt><dd className="mt-1 font-mono text-lg">{v}</dd></div>)}</dl>
              <p className="text-xs text-muted-foreground">Walls are concentrations of contracts, not guaranteed support or resistance. Max pain minimises aggregate intrinsic payout across the selected expiries, not a forecast or price target.</p>
              <a href="https://www.cboe.com/delayed_quotes/gld/quote_table" target="_blank" rel="noopener noreferrer" className="text-sm text-gold underline">CBOE source</a>
            </div>}
            {openReport === "retail" && (
              <div className="space-y-6">
                <dl className="grid grid-cols-2 gap-x-5 gap-y-4 border-b border-border pb-5">
                  {pillarDetail.retail.rows.map(([k, v]) => <div key={k}><dt className="font-mono text-[10px] uppercase text-muted-foreground">{k}</dt><dd className="mt-1 font-mono text-xl tabular-nums">{v}</dd></div>)}
                </dl>
                <p className="text-sm leading-relaxed text-muted-foreground">{pillarDetail.retail.why}</p>
                {(retail?.history.length ?? 0) > 1 ? (
                  <section>
                    <h2 className="font-display text-lg">Retail long share history (%)</h2>
                    <div className="mt-4 h-64 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={(retail?.history ?? []).map((h) => ({ label: new Date(h.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", timeZone: "UTC" }), v: h.longPct }))}>
                          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
                          <XAxis dataKey="label" tick={{ fill: "var(--muted-foreground)", fontSize: 10 }} minTickGap={40} />
                          <YAxis domain={[0, 100]} tick={{ fill: "var(--muted-foreground)", fontSize: 10 }} width={36} />
                          <Tooltip contentStyle={{ background: "var(--popover)", borderColor: "var(--border)", color: "var(--foreground)" }} />
                          <Line dataKey="v" name="Long %" stroke="var(--gold)" dot={false} strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </section>
                ) : <p className="text-xs text-muted-foreground">History builds as new snapshots are captured during market hours.</p>}
                <p className="text-xs text-muted-foreground">Stop and liquidation levels are inferred, never observed broker orders.</p>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
    </div>
  )
}

// Ambient climate light keyed to the final score: gold when neutral,
// soft green or red when moderate, stronger glow when strong.
function ClimateGlow({ score }: { score: number | null }) {
  const s = score ?? 0
  const color = s >= 20 ? "var(--emerald)" : s <= -20 ? "var(--destructive)" : "var(--gold)"
  const strength = Math.abs(s) >= 60 ? 22 : Math.abs(s) >= 20 ? 11 : 9
  const mix = (p: number) => `color-mix(in oklab, ${color} ${p}%, transparent)`
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-0 top-0 h-[900px] transition-[background] duration-700"
      style={{
        backgroundImage: [
          `radial-gradient(ellipse 70% 45% at 50% 0%, ${mix(strength)}, transparent 70%)`,
          `radial-gradient(ellipse 40% 30% at 10% 20%, ${mix(strength * 0.5)}, transparent 70%)`,
          `radial-gradient(ellipse 40% 30% at 90% 20%, ${mix(strength * 0.5)}, transparent 70%)`,
        ].join(", "),
      }}
    />
  )
}

// Instrument-grade confluence gauge. Radial tick ring every 5 points,
// engraved triple-layer track, classification zone markers, a tapered
// metallic needle with a phosphor core, and a gold-ringed pivot hub.
function Gauge({ value }: { value: number | null }) {
  const v = value ?? 0
  const CX = 100
  const CY = 106
  const R = 80
  const ang = (val: number) => Math.PI * (1 - (val + 100) / 200)
  const pt = (val: number, r: number): [number, number] => [CX + r * Math.cos(ang(val)), CY - r * Math.sin(ang(val))]
  const arc = (a0: number, a1: number, r: number) => {
    const [x0, y0] = pt(a0, r)
    const [x1, y1] = pt(a1, r)
    return `M${x0} ${y0} A${r} ${r} 0 ${Math.abs(a1 - a0) > 100 ? 1 : 0} ${a1 > a0 ? 1 : 0} ${x1} ${y1}`
  }

  const ticks = []
  for (let t = -100; t <= 100; t += 5) {
    const major = t % 25 === 0
    const [x1, y1] = pt(t, R + 1)
    const [x2, y2] = pt(t, R - (major ? 9 : 4.5))
    ticks.push(
      <line key={t} x1={x1} y1={y1} x2={x2} y2={y2} stroke={major ? "var(--muted-foreground)" : "var(--border)"} strokeWidth={major ? 1 : 0.5} opacity={major ? 0.75 : 0.5} />,
    )
  }

  const zones = [-60, -40, -20, 20, 40, 60].map((z) => {
    const [x1, y1] = pt(z, R - 11)
    const [x2, y2] = pt(z, R - 20)
    return <line key={z} x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--gold)" strokeWidth={1} opacity={0.55} />
  })

  const a = ang(v)
  const u: [number, number] = [Math.cos(a), -Math.sin(a)]
  const p: [number, number] = [Math.sin(a), Math.cos(a)]
  const tipR = R - 22
  const tip: [number, number] = [CX + u[0] * tipR, CY + u[1] * tipR]
  const mid: [number, number] = [CX + u[0] * tipR * 0.55, CY + u[1] * tipR * 0.55]
  const tail: [number, number] = [CX - u[0] * 12, CY - u[1] * 12]
  const needle = [
    `${tail[0]},${tail[1]}`,
    `${CX + p[0] * 3.4},${CY + p[1] * 3.4}`,
    `${mid[0] + p[0] * 1.1},${mid[1] + p[1] * 1.1}`,
    `${tip[0]},${tip[1]}`,
    `${mid[0] - p[0] * 1.1},${mid[1] - p[1] * 1.1}`,
    `${CX - p[0] * 3.4},${CY - p[1] * 3.4}`,
  ].join(" ")

  const liveColor = v >= 20 ? "var(--emerald)" : v <= -20 ? "var(--destructive)" : "var(--gold)"

  return (
    <svg viewBox="0 0 200 118" className="mx-auto mt-3 w-full max-w-sm" role="img" aria-label={`Confluence gauge at ${value ?? "no data"}`}>
      <defs>
        <linearGradient id="mlg" x1="0" x2="1">
          <stop offset="0%" stopColor="var(--destructive)" />
          <stop offset="50%" stopColor="var(--gold)" />
          <stop offset="100%" stopColor="var(--emerald)" />
        </linearGradient>
        <linearGradient id="mln" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="var(--muted-foreground)" />
          <stop offset="55%" stopColor="var(--foreground)" />
          <stop offset="100%" stopColor="var(--gold)" />
        </linearGradient>
        <clipPath id="mlc">
          <rect x={v >= 0 ? CX : 0} y={0} width={100} height={118} />
        </clipPath>
      </defs>

      {/* Engraved triple-layer track */}
      <path d={arc(-100, 100, R - 6)} fill="none" stroke="var(--border)" strokeWidth={13} opacity={0.45} />
      <path d={arc(-100, 100, R - 6)} fill="none" stroke="url(#mlg)" strokeWidth={8} opacity={0.3} />
      <path d={arc(-100, 100, R - 6)} fill="none" stroke="var(--background)" strokeWidth={1} opacity={0.6} />

      {ticks}
      {zones}

      {/* Active scored range: flat edge exactly on zero, rounded tip at the live value */}
      {value != null && v !== 0 && (
        <g clipPath="url(#mlc)" style={{ filter: `drop-shadow(0 0 5px color-mix(in oklab, ${liveColor} 65%, transparent))` }}>
          <path d={arc(0, v, R - 6)} fill="none" stroke={liveColor} strokeWidth={4} strokeLinecap="butt" opacity={0.9} />
          <circle cx={pt(v, R - 6)[0]} cy={pt(v, R - 6)[1]} r={2} fill={liveColor} opacity={0.9} />
        </g>
      )}

      {/* Tapered needle with phosphor core */}
      {value != null && (
        <>
          <polygon points={needle} fill="url(#mln)" opacity={0.95} style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))" }} />
          <line x1={CX} y1={CY} x2={tip[0]} y2={tip[1]} stroke="var(--gold)" strokeWidth={0.7} opacity={0.85} />
        </>
      )}

      {/* Gold-ringed pivot hub */}
      <circle cx={CX} cy={CY} r={7.5} fill="var(--background)" stroke="var(--border)" strokeWidth={1} />
      <circle cx={CX} cy={CY} r={5} fill="none" stroke="var(--gold)" strokeWidth={1.2} opacity={0.9} />
      <circle cx={CX} cy={CY} r={1.6} fill="var(--foreground)" />

      <text x={pt(-100, R + 10)[0]} y={pt(-100, R + 10)[1] + 6} fontSize={7} fill="var(--muted-foreground)" fontFamily="monospace" textAnchor="middle">-100</text>
      <text x={pt(100, R + 10)[0]} y={pt(100, R + 10)[1] + 6} fontSize={7} fill="var(--muted-foreground)" fontFamily="monospace" textAnchor="middle">+100</text>
      <text x={CX} y={pt(0, R + 9)[1] + 2} fontSize={7} fill="var(--muted-foreground)" fontFamily="monospace" textAnchor="middle">0</text>
    </svg>
  )
}

function Spark({ data }: { data: number[] }) {
  const min = Math.min(...data)
  const max = Math.max(...data)
  const r = max - min || 1
  const pts = data.map((d, i) => `${(i / (data.length - 1)) * 60},${20 - ((d - min) / r) * 18 - 1}`).join(" ")
  const up = data[data.length - 1] >= data[0]
  return (
    <svg viewBox="0 0 60 20" className="h-5 w-16" aria-hidden="true">
      <polyline points={pts} fill="none" stroke={up ? "var(--emerald)" : "var(--destructive)"} strokeWidth="1.2" />
    </svg>
  )
}

function SimSlider({ label, value, min, max, step = 1, unit, onChange }: { label: string; value: number; min: number; max: number; step?: number; unit: string; onChange: (v: number) => void }) {
  return (
    <label className="block">
      <div className="flex justify-between font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
        <span>{label}</span>
        <span className="tabular-nums text-foreground">{value > 0 && unit === " pp" ? "+" : ""}{value}{unit}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="mt-2 w-full accent-[var(--gold)]" />
    </label>
  )
}

function ScoreBar({ value }: { value: number | null }) {
  const w = value == null ? 0 : Math.abs(value) / 2
  return (
    <div className="relative mt-2 h-1.5 rounded-full bg-muted">
      <span className="absolute inset-y-0 left-1/2 w-px bg-border" aria-hidden="true" />
      {value != null && (
        <span
          className={`absolute inset-y-0 rounded-full ${value >= 0 ? "bg-emerald" : "bg-destructive"}`}
          style={value >= 0 ? { left: "50%", width: `${w}%` } : { right: "50%", width: `${w}%` }}
        />
      )}
    </div>
  )
}
